
import { createContext, useContext, useMemo, useReducer, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

import { SETTINGS_INITIAL_CONTEXT, SETTINGS_INITIAL_STATE } from "./SettingsProviderConstants"
import { ISettingsContext, ISettingsProviderProps, TTab } from "./SettingsProviderTypes"
import useGetLaundryScheduleInterval from "api/querries/laundry/useGetLaundryScheduleInterval"
import { useAppContext } from "providers/AppProvider/AppProvider"
import useChangeLaundryScheduleIntervalMutation from "api/mutations/laundry/useChangeLaundryScheduleInterval"
import { useUI } from "providers/UIProvider"
import { UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"
import useGetLaundryCommission from "api/querries/laundry/useGetLaundryCommission"
import useChangeLaundryCommissionMutation from "api/mutations/laundry/useChangeLaundryCommission"
import { IChangeLaundryCancelCommissionsBody, IChangeLaundryCommissionBody, IGetLaundryCancelCommissionData, ILaundryCancelCommission } from "types/laundry"
import useGetLaundryCancelCommissions from "api/querries/laundry/useGetLaundryCancelCommissions"
import useCancellationFee from "hooks/useCancellationFee"
import useChangeLaundryCancelCommissionsMutation from "api/mutations/laundry/useChangeLaundryCancelCommissions"
import { BOOKING_FEE_TABS } from "types/shared"

export const SettingsContext = createContext<ISettingsContext>(SETTINGS_INITIAL_CONTEXT)

export const useSettings: () => ISettingsContext = () => useContext(SettingsContext)

const LAUNDRY_URL_REGEX = /laundries\/[0-9]/

const SettingsProvider: React.FC<ISettingsProviderProps> = ({ children }) => {
  const { pathname } = useLocation()

  const { toggleModal } = useUI()
  const { isAdmin, refetchLaundryBranches, selectedBranches, updateSelectedBranches } = useAppContext()
  const { form: { resetForm: resetCanncellationFeeForm } } = useCancellationFee()
  const isCancellationCommissionPage = pathname === "/settings/cancellation_fee"
  const isAddLaundryPage = pathname === "/laundries/add-laundry"

  const fetchScheduleInterval = Boolean(isAdmin && (pathname.match(LAUNDRY_URL_REGEX) || pathname === "/settings/bookings"))
  const fetchCommission = Boolean(isAdmin && (pathname.match(LAUNDRY_URL_REGEX) || pathname === "/settings/booking_fee"))
  const fetchCancelCommissions = Boolean(isAdmin && (pathname.match(LAUNDRY_URL_REGEX) || isCancellationCommissionPage))
  const customSelectedBranches = selectedBranches.length === 1 ? selectedBranches[0] : null
  const laundryId = (isAdmin && pathname.match(LAUNDRY_URL_REGEX)) ? Number(pathname.split("/").reverse()[0]) || null : customSelectedBranches || null
  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    SETTINGS_INITIAL_STATE,
  )
  const [laundryIds, setLaundryIds] = useState<number[]>([])

  const { data: laundryScheduleIntervalData, isFetched: laundryScheduleIntervalFetched, refetch: refetchLaundryScheduleInterval } = useGetLaundryScheduleInterval(laundryId, (fetchScheduleInterval && laundryId !== null))
  const { mutate: changeLaundryScheduleInterval, isLoading: changeLaundryScheduleIntervalLoading, isSuccess: changeLaundryScheduleIntervalSuccess } = useChangeLaundryScheduleIntervalMutation()
  const { data: laundryCommissionData, isFetched: laundryCommissionFetched, refetch: refetchLaundryCommission } = useGetLaundryCommission(laundryId, (fetchCommission && laundryId !== null))
  const { mutate: changeLaundryCommission, isLoading: changeLaundryCommissionLoading, isSuccess: changeLaundryCommissionSuccess } = useChangeLaundryCommissionMutation()
  const {
    data: laundryCancelCommissionsData,
    isFetched: laundryCancelCommissinsFetched,
    refetch: refetchLaundryCancelCommissions
  } = useGetLaundryCancelCommissions(laundryIds, fetchCancelCommissions)
  const { mutate: changeLaundryCancelCommissions, isLoading: changeLaundryCancelCommissionsLoading, isSuccess: changeLaundryCancelCommissionsSuccess } = useChangeLaundryCancelCommissionsMutation()
  const handleLanguageTabChange = (newTab: TTab) => setState({ languageSettings: { currentTab: newTab } })
  const handleChangeCommissionTab = (newTab: BOOKING_FEE_TABS) => setState({ commissionTabType: newTab })

  const handleChangeCancelCommissionsTab = (newTab: BOOKING_FEE_TABS) => setState({ cancelCommissionsTabType: newTab })

  const handleChangeCancelCommission = (newCancelCommissions: ILaundryCancelCommission[]) => {
    setState({ cancelCommissions: newCancelCommissions })
  }
  const handleChangeScheduleIntervalSubmit = (laundryIds: number[], interval: number) => {
    changeLaundryScheduleInterval({ carWashIds: laundryIds, scheduleInterval: interval })
  }
  const handleChangeLaundryCommissionSubmit = (laundryIds: number[], commissionAmount: number) => {
    const tabType = state.commissionTabType
    const data: IChangeLaundryCommissionBody = {
      carWashIds: laundryIds,
      commission: commissionAmount,
      commissionType: tabType === BOOKING_FEE_TABS.PERCENT ? 1 : 2
    }
    changeLaundryCommission({ ...data })
  }
  const handleChangeCancelCommissionsSubmit = (laundryIds: number[], commissionsDataArray: ILaundryCancelCommission[]) => {
    const data: IChangeLaundryCancelCommissionsBody = {
      carWashIds: laundryIds,
      commissionData: commissionsDataArray.map((commissionData) => ({
        sortId: commissionData.sortId,
        commissionType: commissionData.commissionType,
        commissionTypeString: commissionData.commissionTypeString,
        commission: commissionData.commission as number,
        cancelBeforeMinute: commissionData.cancelBeforeMinute as number
      }))
    }
    changeLaundryCancelCommissions(data)
  }

  const updateCancelCommissionsHistory = (cancelCommissionsHistory: IGetLaundryCancelCommissionData[]) => setState({ cancelCommissionsHistory })

  useEffect(() => {
    if (laundryScheduleIntervalData && laundryScheduleIntervalFetched && !isAddLaundryPage) {
      setState({ scheduleInterval: laundryScheduleIntervalData.data })
    }
  }
    , [laundryScheduleIntervalData, isAddLaundryPage])


  useEffect(() => {
    if (changeLaundryScheduleIntervalSuccess) {
      if (pathname === "/settings/bookings") {
        toggleModal("feeHistoryModal", false, UI_INITIAL_STATE.modals.feeHistoryModal.data)
      } else {
        toggleModal("reservationSettingsModal", false)
        refetchLaundryScheduleInterval()
      }

      refetchLaundryBranches()
      updateSelectedBranches([])
    }
  }, [changeLaundryScheduleIntervalSuccess])

  useEffect(() => {
    if (laundryCommissionData && laundryCommissionFetched && !isAddLaundryPage) {
      setState({
        commission: laundryCommissionData.data,
        commissionTabType: laundryCommissionData.data?.commissionType === 2 ? BOOKING_FEE_TABS.FIXED : BOOKING_FEE_TABS.PERCENT,
      })
    }
  }, [laundryCommissionData, isAddLaundryPage])

  useEffect(() => {
    if (changeLaundryCommissionSuccess) {
      if (pathname === "/settings/booking_fee") {
        toggleModal("feeHistoryModal", false, UI_INITIAL_STATE.modals.feeHistoryModal.data)
        setState({ commission: null })
      } else {
        toggleModal("reservationCommissionModal", false)
        refetchLaundryCommission()
      }
      refetchLaundryBranches()
    }
  }, [changeLaundryCommissionSuccess])

  useEffect(() => {
    if (laundryCancelCommissionsData && laundryCancelCommissinsFetched && !isAddLaundryPage) {
      if (isCancellationCommissionPage) {
        setState({
          cancelCommissionsHistory: laundryCancelCommissionsData.data,
          cancelCommissions: laundryCancelCommissionsData.data[0]?.commissionData || [],
          cancelCommissionsTabType: laundryCancelCommissionsData.data[0]?.commissionData[0]?.commissionType === 2 ? BOOKING_FEE_TABS.FIXED : BOOKING_FEE_TABS.PERCENT
        })

      } else {
        resetCanncellationFeeForm()
        setState({
          cancelCommissions: laundryCancelCommissionsData.data[0]?.commissionData || [],
          cancelCommissionsTabType: laundryCancelCommissionsData.data[0]?.commissionData[0]?.commissionType === 2 ? BOOKING_FEE_TABS.FIXED : BOOKING_FEE_TABS.PERCENT
        })
      }
    }
  }, [laundryCancelCommissionsData, isAddLaundryPage])

  useEffect(() => {
    if (changeLaundryCancelCommissionsSuccess) {
      if (changeLaundryCancelCommissionsSuccess) {
        if (isCancellationCommissionPage) {
          toggleModal("feeHistoryModal", false, UI_INITIAL_STATE.modals.feeHistoryModal.data)
          setState({ cancelCommissions: [] })
        } else {
          toggleModal("cancelReservationCommissionModal", false, UI_INITIAL_STATE.modals.cancelReservationCommissionModal.data)
          refetchLaundryCancelCommissions()
        }
      }
    }
  }, [changeLaundryCancelCommissionsSuccess])

  useEffect(() => {
    if (selectedBranches && isCancellationCommissionPage) {
      setLaundryIds(selectedBranches)
    }
  }, [selectedBranches, pathname])

  useEffect(() => {
    if (laundryId && pathname.match(LAUNDRY_URL_REGEX)) {
      setLaundryIds([laundryId])
    }
  }, [laundryId])

  useEffect(() => {
    if (isAddLaundryPage) {
      resetCanncellationFeeForm()
      setState({
        commission: {
          commission: null,
          commissionTabType: BOOKING_FEE_TABS.FIXED,
        },
        scheduleInterval: null,
        cancelCommissions: [],
      })
    }
  }, [isAddLaundryPage])

  const value = useMemo(() => ({
    ...state,
    handleChangeCommissionTab,
    handleChangeCancelCommissionsTab,
    updateCancelCommissionsHistory,
    handleChangeCancelCommission,
    handleChangeScheduleIntervalSubmit,
    handleChangeLaundryCommissionSubmit,
    handleChangeCancelCommissionsSubmit,
    changeLaundryScheduleIntervalLoading,
    changeLaundryCommissionLoading,
    changeLaundryCancelCommissionsLoading,
    languageSettings: {
      ...state.languageSettings,
      handleTabChange: handleLanguageTabChange
    }
  }), [state, changeLaundryScheduleIntervalLoading, changeLaundryCommissionLoading, changeLaundryCancelCommissionsLoading])

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}

export default SettingsProvider